// ──────────────────────────────────────────────
// RecordingService — continuous MediaRecorder with init-segment prepending
//
// Replaces the per-chunk RecordRTC approach with a single continuous
// MediaRecorder using native `timeslice`. Every chunk is made independently
// decodable by prepending the init segment extracted from chunk 0.
//
// See ADR-006 for design rationale.
//
// Usage:
//   const recording = new RecordingService(config);
//   await recording.start(stream, sessionId);
//   // ... chunks are persisted + enqueued automatically
//   await recording.stop();
//   recording.dispose();
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import type { StorageService } from '../storage';
import type { UploadService } from '../upload';
import { BaseService } from '../upload/BaseService';

import { extractInitSegment, prependInitSegment } from './InitSegmentExtractor';
import type {
  RecordingEventMap,
  RecordingServiceConfig,
  RecordingServiceStatus,
  RecordingState,
} from './types';
import {
  DEFAULT_MAX_CHUNK_BYTES,
  DEFAULT_REQUEST_DATA_INTERVAL_MS,
  DEFAULT_TIMESLICE_MS,
  getSupportedMimeType,
  INIT_SEGMENT_CHUNK_INDEX,
  RECORDING_LOCK_NAME,
  STAGING_CHUNK_INDEX,
} from './types';

// ── Helpers ──

function initialRecordingState(): RecordingState {
  return {
    status: 'idle',
    duration: 0,
    chunkCount: 0,
    activeDeviceId: null,
    mimeType: null,
    initSegmentCaptured: false,
    error: null,
  };
}

let chunkIdCounter = 0;
function nextChunkId(): string {
  return `chunk-${Date.now()}-${++chunkIdCounter}`;
}

/** Audio level polling interval — matches WebRecordingService's 50ms cadence */
const AUDIO_LEVEL_POLL_MS = 50;
/** AnalyserNode FFT size — matches AudioHealthMonitor */
const ANALYSER_FFT_SIZE = 32;

/**
 * RecordingService — owns the MediaRecorder lifecycle, init-segment
 * extraction/prepending, crash-recovery writes, and chunk finalization.
 *
 * Consumes StorageService for persistence and UploadService for upload.
 * Framework-agnostic: React connects via useSyncExternalStore.
 */
export class RecordingService extends BaseService<RecordingState, RecordingEventMap> {
  private readonly storageService: StorageService;
  private readonly uploadService: UploadService;
  private readonly timesliceMs: number;
  private readonly requestDataIntervalMs: number;
  private readonly maxChunkBytes: number;

  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private sessionId: string | null = null;

  // Init-segment state
  private initSegment: ArrayBuffer | null = null;
  private mimeType: string | null = null;
  private isFirstChunk = true;

  // Chunk accumulation between upload boundaries
  private pendingChunks: ArrayBuffer[] = [];
  private pendingBytesSize = 0;

  // Timers
  private requestDataTimerId: ReturnType<typeof setInterval> | null = null;
  private uploadBoundaryTimerId: ReturnType<typeof setInterval> | null = null;
  private durationTimerId: ReturnType<typeof setInterval> | null = null;
  private recordingStartTime = 0;
  private pausedDuration = 0;
  private pauseStartTime = 0;

  // Chunk index counter (0-based, incremented on each finalized upload)
  private nextChunkIndex = 0;

  // Web Lock
  private lockRelease: (() => void) | null = null;

  // Wake Lock
  private wakeLock: WakeLockSentinel | null = null;

  // Lifecycle
  private disposed = false;

  // Audio level analysis
  private levelAudioContext: AudioContext | null = null;
  private levelAnalyser: AnalyserNode | null = null;
  private levelSourceNode: MediaStreamAudioSourceNode | null = null;
  private audioLevelTimerId: ReturnType<typeof setInterval> | null = null;
  private analyserDataArray: Uint8Array | null = null;

  // Visibility handler ref for cleanup
  private readonly handleVisibilityChange: () => void;

  constructor(config: RecordingServiceConfig) {
    super(initialRecordingState());

    this.storageService = config.storageService;
    this.uploadService = config.uploadService;
    this.timesliceMs = config.timesliceMs ?? DEFAULT_TIMESLICE_MS;
    this.requestDataIntervalMs = config.requestDataIntervalMs ?? DEFAULT_REQUEST_DATA_INTERVAL_MS;
    this.maxChunkBytes = config.maxChunkBytes ?? DEFAULT_MAX_CHUNK_BYTES;

    this.handleVisibilityChange = this.onVisibilityChange.bind(this);
  }

  // ── Public API ──

  /**
   * Start recording from the given MediaStream.
   *
   * Acquires Web Lock (cross-tab exclusion), Wake Lock (prevent sleep),
   * creates MediaRecorder with timeslice, and starts the dual-interval pipeline.
   */
  async start(stream: MediaStream, sessionId: string): Promise<void> {
    if (this.disposed) throw new Error('RecordingService is disposed');
    if (this.state.status !== 'idle' && this.state.status !== 'error') {
      throw new Error(`Cannot start recording in state: ${this.state.status}`);
    }

    this.stream = stream;
    this.sessionId = sessionId;
    this.isFirstChunk = true;
    this.initSegment = null;
    this.pendingChunks = [];
    this.pendingBytesSize = 0;
    this.nextChunkIndex = 0;
    this.pausedDuration = 0;
    this.pauseStartTime = 0;

    // Detect MIME type
    this.mimeType = getSupportedMimeType() ?? null;
    if (!this.mimeType) {
      this.handleError('No supported audio MIME type found for MediaRecorder');
      return;
    }

    // Acquire Web Lock for cross-tab exclusion
    const lockAcquired = await this.acquireWebLock();
    if (!lockAcquired) {
      this.handleError('Another tab is already recording (Web Lock held)');
      return;
    }

    // Acquire Wake Lock
    await this.acquireWakeLock();

    try {
      // Create and start MediaRecorder
      this.recorder = new MediaRecorder(stream, { mimeType: this.mimeType });
      this.setupRecorderHandlers();

      // Extract device ID from stream track
      const audioTrack = stream.getAudioTracks()[0];
      const deviceId = audioTrack?.getSettings()?.deviceId ?? null;

      // Start recording with timeslice — produces ondataavailable every timesliceMs
      this.recorder.start(this.timesliceMs);

      // Start timers
      this.recordingStartTime = performance.now();
      this.startDurationTimer();
      this.startRequestDataTimer();
      this.startUploadBoundaryTimer();
      this.startAudioLevelAnalysis(stream);

      // Listen for visibility changes (flush on hidden, re-acquire wake lock on visible)
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      // Update state
      this.setServiceStatus('recording');
      this.updateState({
        activeDeviceId: deviceId,
        mimeType: this.mimeType,
        error: null,
      });

      SentryService.addBreadcrumb({
        category: 'recording.service',
        message: `Recording started (mimeType=${this.mimeType}, timeslice=${this.timesliceMs}ms)`,
        level: 'info',
        data: { sessionId, deviceId },
      });
    } catch (err) {
      this.releaseWebLock();
      this.releaseWakeLock();
      this.handleError(
        `Failed to start MediaRecorder: ${err instanceof Error ? err.message : String(err)}`,
        err,
      );
    }
  }

  /** Stop recording and finalize any remaining chunks. */
  async stop(): Promise<void> {
    if (this.state.status !== 'recording' && this.state.status !== 'paused') {
      return;
    }

    this.setServiceStatus('stopping');

    // Stop timers first
    this.stopTimers();

    // Stop MediaRecorder — triggers final ondataavailable
    const recorder = this.recorder;
    if (recorder && recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const onStop = () => {
          recorder.removeEventListener('stop', onStop);
          resolve();
        };
        recorder.addEventListener('stop', onStop);
        recorder.stop();
      });
    }

    // Finalize any remaining accumulated chunks
    await this.finalizeAndUpload();

    // Cleanup
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.releaseWakeLock();
    this.releaseWebLock();
    this.recorder = null;

    this.setServiceStatus('idle');

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Recording stopped (chunks=${this.state.chunkCount})`,
      level: 'info',
      data: { sessionId: this.sessionId },
    });
  }

  /** Pause the MediaRecorder. */
  pause(): void {
    if (this.state.status !== 'recording') return;

    if (this.recorder && this.recorder.state === 'recording') {
      this.recorder.pause();
    }

    this.pauseStartTime = performance.now();
    this.stopTimers();
    this.releaseWakeLock();
    this.setServiceStatus('paused');

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'Recording paused',
      level: 'info',
    });
  }

  /**
   * Resume from a cold start (e.g., tab reload while recording was paused).
   * Creates a new MediaRecorder without resetting chunk state.
   * Enters 'paused' state — caller must call resume() to begin recording.
   */
  async resumeFromColdStart(
    stream: MediaStream,
    sessionId: string,
    nextChunkIndex: number,
  ): Promise<void> {
    if (this.recorder) {
      throw new Error('Cannot cold-resume: recorder already exists');
    }

    this.stream = stream;
    this.sessionId = sessionId;
    this.nextChunkIndex = nextChunkIndex;
    this.pendingChunks = [];
    this.pendingBytesSize = 0;

    // Restore init segment from storage
    const stored = await this.storageService.readChunk(sessionId, INIT_SEGMENT_CHUNK_INDEX);
    if (stored) {
      this.initSegment = stored;
      this.isFirstChunk = false;
    } else {
      this.isFirstChunk = true;
      this.initSegment = null;
    }

    // Determine MIME type
    this.mimeType = getSupportedMimeType() ?? null;
    if (!this.mimeType) {
      throw new Error('No supported MIME type for MediaRecorder');
    }

    // Create recorder (inactive — not started yet)
    this.recorder = new MediaRecorder(stream, { mimeType: this.mimeType });
    this.setupRecorderHandlers();

    // Acquire Web Lock for cross-tab exclusion
    await this.acquireWebLock();

    // Set paused state — user will click resume to start
    this.setServiceStatus('paused');
    this.updateState({
      activeDeviceId: stream.getAudioTracks()[0]?.getSettings().deviceId ?? null,
      mimeType: this.mimeType,
      initSegmentCaptured: this.initSegment !== null,
    });

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Cold-resumed session ${sessionId} at chunk ${nextChunkIndex}`,
      level: 'info',
    });
  }

  /** Resume the MediaRecorder from paused state. */
  async resume(): Promise<void> {
    if (this.state.status !== 'paused') return;

    if (this.pauseStartTime > 0) {
      this.pausedDuration += performance.now() - this.pauseStartTime;
      this.pauseStartTime = 0;
    }

    // Resume or start MediaRecorder
    if (this.recorder) {
      if (this.recorder.state === 'inactive') {
        // Cold-resumed: recorder was created but never started
        this.recorder.start(this.timesliceMs);
      } else if (this.recorder.state === 'paused') {
        this.recorder.resume();
      }
    }

    this.startDurationTimer();
    this.startRequestDataTimer();
    this.startUploadBoundaryTimer();
    if (this.stream) {
      this.startAudioLevelAnalysis(this.stream);
    }
    await this.acquireWakeLock();

    // Register visibility listener (may be missing after cold-resume)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.setServiceStatus('recording');

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'Recording resumed',
      level: 'info',
    });
  }

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.stopTimers();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop();
      } catch {
        // Ignore — recorder may already be stopped
      }
    }
    this.recorder = null;

    this.releaseWakeLock();
    this.releaseWebLock();

    this.pendingChunks = [];
    this.initSegment = null;
    this.stream = null;
    this.sessionId = null;

    this.setState(initialRecordingState());

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'RecordingService disposed',
      level: 'info',
    });

    super.dispose();
  }

  // ── MediaRecorder Event Handlers ──

  private setupRecorderHandlers(): void {
    if (!this.recorder) return;

    this.recorder.ondataavailable = (event: BlobEvent) => {
      void this.handleDataAvailable(event);
    };

    this.recorder.onerror = () => {
      this.handleRecorderError();
    };
  }

  private async handleDataAvailable(event: BlobEvent): Promise<void> {
    if (event.data.size === 0) return; // Safari can produce zero-byte chunks

    try {
      const arrayBuffer = await event.data.arrayBuffer();

      if (this.isFirstChunk) {
        // Chunk 0: extract init segment
        this.isFirstChunk = false;
        await this.processFirstChunk(arrayBuffer);
      } else {
        // Subsequent chunk: accumulate for the upload boundary
        this.pendingChunks.push(arrayBuffer);
        this.pendingBytesSize += arrayBuffer.byteLength;

        // Write to staging key for crash recovery
        await this.writeStagingChunk();
      }
    } catch (err) {
      const message = `Error processing chunk: ${err instanceof Error ? err.message : String(err)}`;
      SentryService.captureException(err instanceof Error ? err : new Error(message));
      this.emit('chunkError', { chunkId: 'unknown', error: message });
    }
  }

  private async processFirstChunk(data: ArrayBuffer): Promise<void> {
    if (!this.mimeType || !this.sessionId) return;

    // Extract init segment
    this.initSegment = await extractInitSegment(data, this.mimeType);

    // Persist init segment to StorageService for crash recovery
    await this.storageService.writeChunk(this.sessionId, INIT_SEGMENT_CHUNK_INDEX, this.initSegment);

    this.updateState({ initSegmentCaptured: true });
    this.emit('initSegmentCaptured', {
      mimeType: this.mimeType,
      byteSize: this.initSegment.byteLength,
    });

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Init segment captured (${this.initSegment.byteLength} bytes)`,
      level: 'info',
    });

    // Chunk 0 is already independently decodable — finalize directly
    this.pendingChunks.push(data);
    this.pendingBytesSize += data.byteLength;

    // Write to staging for crash recovery
    await this.writeStagingChunk();
  }

  private handleRecorderError(): void {
    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'MediaRecorder error — attempting recovery',
      level: 'error',
    });

    // MediaRecorder transitions to inactive on error.
    // Recovery: create new MediaRecorder against the same stream.
    if (this.stream && this.stream.active && this.sessionId) {
      try {
        const mimeType = this.mimeType ?? getSupportedMimeType();
        if (!mimeType) {
          this.handleError('Cannot recover MediaRecorder — no supported MIME type');
          return;
        }

        this.recorder = new MediaRecorder(this.stream, { mimeType });
        this.setupRecorderHandlers();

        // Fresh init segment needed from the new recorder
        this.isFirstChunk = true;
        this.initSegment = null;
        this.updateState({ initSegmentCaptured: false });

        this.recorder.start(this.timesliceMs);

        SentryService.addBreadcrumb({
          category: 'recording.service',
          message: 'MediaRecorder recovered successfully',
          level: 'info',
        });
      } catch (err) {
        this.handleError(
          `MediaRecorder recovery failed: ${err instanceof Error ? err.message : String(err)}`,
          err,
        );
      }
    } else {
      this.handleError('Cannot recover MediaRecorder — stream is no longer active');
    }
  }

  // ── Chunk Pipeline ──

  /**
   * Write all accumulated chunk data to the staging key for crash recovery.
   * Concatenates pendingChunks so a crash loses at most ~2s of audio
   * regardless of timeslice length.
   */
  private async writeStagingChunk(): Promise<void> {
    if (!this.sessionId || this.pendingChunks.length === 0) return;
    try {
      let data: ArrayBuffer;
      if (this.pendingChunks.length === 1) {
        data = this.pendingChunks[0];
      } else {
        const totalSize = this.pendingChunks.reduce((sum, c) => sum + c.byteLength, 0);
        const merged = new Uint8Array(totalSize);
        let offset = 0;
        for (const chunk of this.pendingChunks) {
          merged.set(new Uint8Array(chunk), offset);
          offset += chunk.byteLength;
        }
        data = merged.buffer;
      }
      await this.storageService.writeChunk(this.sessionId, STAGING_CHUNK_INDEX, data);
    } catch {
      // Staging writes are best-effort — don't fail the recording
    }
  }

  /**
   * Finalize accumulated chunks: concatenate, prepend init segment,
   * persist to StorageService, and enqueue to UploadService.
   */
  private async finalizeAndUpload(): Promise<void> {
    if (!this.sessionId || this.pendingChunks.length === 0) return;

    const chunkIndex = this.nextChunkIndex++;
    const chunkId = nextChunkId();
    const contentType = this.mimeType ?? 'audio/webm';

    try {
      // Concatenate pending chunks
      let data: ArrayBuffer;
      if (this.pendingChunks.length === 1) {
        data = this.pendingChunks[0];
      } else {
        const totalSize = this.pendingChunks.reduce((sum, c) => sum + c.byteLength, 0);
        const merged = new Uint8Array(totalSize);
        let offset = 0;
        for (const chunk of this.pendingChunks) {
          merged.set(new Uint8Array(chunk), offset);
          offset += chunk.byteLength;
        }
        data = merged.buffer;
      }

      // Prepend init segment for chunks after chunk 0
      if (chunkIndex > 0 && this.initSegment) {
        data = prependInitSegment(this.initSegment, data);
      }

      const byteSize = data.byteLength;

      // Persist to StorageService at permanent chunk index
      await this.storageService.writeChunk(this.sessionId, chunkIndex, data);

      // Clean up staging data
      try {
        await this.storageService.deleteChunk(this.sessionId, STAGING_CHUNK_INDEX);
      } catch {
        // Best-effort cleanup
      }

      // Enqueue to UploadService (fire-and-forget)
      this.uploadService.enqueue({
        sessionId: this.sessionId,
        chunkIndex,
        chunkId,
        contentType,
        byteSize,
      });

      // Update state
      this.updateState({ chunkCount: this.state.chunkCount + 1 });

      // Emit event for RecordingContext integration
      this.emit('chunkReady', {
        sessionId: this.sessionId,
        chunkIndex,
        chunkId,
        contentType,
        byteSize,
      });

      SentryService.addBreadcrumb({
        category: 'recording.service',
        message: `Chunk ${chunkIndex} finalized (${byteSize} bytes)`,
        level: 'info',
        data: { chunkId, sessionId: this.sessionId },
      });
    } catch (err) {
      const message = `Failed to finalize chunk ${chunkIndex}: ${err instanceof Error ? err.message : String(err)}`;
      SentryService.captureException(err instanceof Error ? err : new Error(message));
      this.emit('chunkError', { chunkId, error: message });
    }

    // Reset accumulation
    this.pendingChunks = [];
    this.pendingBytesSize = 0;
  }

  // ── Timers ──

  private startDurationTimer(): void {
    this.stopDurationTimer();
    this.durationTimerId = setInterval(() => {
      const elapsed = performance.now() - this.recordingStartTime - this.pausedDuration;
      this.updateState({ duration: Math.floor(elapsed / 1000) });
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationTimerId !== null) {
      clearInterval(this.durationTimerId);
      this.durationTimerId = null;
    }
  }

  /**
   * Start the 2s requestData() timer for crash recovery.
   * This forces MediaRecorder to fire ondataavailable, giving us
   * fresh data to persist. At most ~2s of audio lost on crash.
   */
  private startRequestDataTimer(): void {
    this.stopRequestDataTimer();
    this.requestDataTimerId = setInterval(() => {
      if (this.recorder && this.recorder.state === 'recording') {
        try {
          this.recorder.requestData();
        } catch {
          // requestData can throw if recorder is in wrong state
        }
      }
    }, this.requestDataIntervalMs);
  }

  private stopRequestDataTimer(): void {
    if (this.requestDataTimerId !== null) {
      clearInterval(this.requestDataTimerId);
      this.requestDataTimerId = null;
    }
  }

  /**
   * Start the 30s upload boundary timer.
   * On each tick, finalize accumulated chunks and enqueue to UploadService.
   */
  private startUploadBoundaryTimer(): void {
    this.stopUploadBoundaryTimer();
    this.uploadBoundaryTimerId = setInterval(() => {
      void this.finalizeAndUpload();
    }, this.timesliceMs);
  }

  private stopUploadBoundaryTimer(): void {
    if (this.uploadBoundaryTimerId !== null) {
      clearInterval(this.uploadBoundaryTimerId);
      this.uploadBoundaryTimerId = null;
    }
  }

  private stopTimers(): void {
    this.stopDurationTimer();
    this.stopRequestDataTimer();
    this.stopUploadBoundaryTimer();
    this.stopAudioLevelAnalysis();
  }

  // ── Audio Level Analysis (feeds UI meter via audioLevel events) ──

  private startAudioLevelAnalysis(stream: MediaStream): void {
    // Stop any previous analysis (e.g. from a previous start before stop)
    this.stopAudioLevelAnalysis();

    try {
      this.levelAudioContext = new AudioContext();
      this.levelAnalyser = this.levelAudioContext.createAnalyser();
      this.levelAnalyser.fftSize = ANALYSER_FFT_SIZE;
      this.levelAnalyser.smoothingTimeConstant = 0;
      this.analyserDataArray = new Uint8Array(this.levelAnalyser.frequencyBinCount);

      this.levelSourceNode = this.levelAudioContext.createMediaStreamSource(stream);
      this.levelSourceNode.connect(this.levelAnalyser);

      this.audioLevelTimerId = setInterval(() => {
        if (!this.levelAnalyser || !this.analyserDataArray) return;

        this.levelAnalyser.getByteFrequencyData(this.analyserDataArray);
        let sum = 0;
        for (let i = 0; i < this.analyserDataArray.length; i++) {
          sum += this.analyserDataArray[i];
        }
        const average = sum / this.analyserDataArray.length;

        this.emit('audioLevel', { level: average });
      }, AUDIO_LEVEL_POLL_MS);
    } catch (err) {
      // Audio analysis is non-critical — log but don't fail the recording
      SentryService.addBreadcrumb({
        category: 'recording.service',
        message: `Failed to start audio level analysis: ${err instanceof Error ? err.message : String(err)}`,
        level: 'warning',
      });
    }
  }

  private stopAudioLevelAnalysis(): void {
    if (this.audioLevelTimerId !== null) {
      clearInterval(this.audioLevelTimerId);
      this.audioLevelTimerId = null;
    }

    if (this.levelSourceNode) {
      try {
        this.levelSourceNode.disconnect();
      } catch {
        /* may already be disconnected */
      }
      this.levelSourceNode = null;
    }

    if (this.levelAnalyser) {
      try {
        this.levelAnalyser.disconnect();
      } catch {
        /* may already be disconnected */
      }
      this.levelAnalyser = null;
    }

    if (this.levelAudioContext) {
      if (this.levelAudioContext.state !== 'closed') {
        void this.levelAudioContext.close();
      }
      this.levelAudioContext = null;
    }

    this.analyserDataArray = null;
  }

  // ── Web Lock (cross-tab exclusion) ──

  private async acquireWebLock(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return true; // Feature not available — skip
    }

    try {
      return await new Promise<boolean>((resolve) => {
        navigator.locks.request(
          RECORDING_LOCK_NAME,
          { ifAvailable: true },
          (lock) => {
            if (!lock) {
              // Lock not available — another tab is recording
              resolve(false);
              return Promise.resolve();
            }

            // Lock acquired — hold it via a never-resolving promise
            // Release happens when we resolve the stored callback
            return new Promise<void>((release) => {
              this.lockRelease = release;
              resolve(true);
            });
          },
        );
      });
    } catch {
      // Web Lock API error — proceed without lock
      return true;
    }
  }

  private releaseWebLock(): void {
    if (this.lockRelease) {
      this.lockRelease();
      this.lockRelease = null;
    }
  }

  // ── Wake Lock (prevent device sleep) ──

  private async acquireWakeLock(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.wakeLock) return;

    try {
      this.wakeLock = await navigator.wakeLock.request('screen');

      SentryService.addBreadcrumb({
        category: 'recording.wakelock',
        message: 'Wake lock acquired',
        level: 'info',
      });

      this.wakeLock.addEventListener('release', () => {
        // Browser auto-releases when page becomes hidden; this also fires
        // if iOS overrides the lock (low battery, system pressure).
        SentryService.addBreadcrumb({
          category: 'recording.wakelock',
          message: `Wake lock released (recording=${this.state.status})`,
          level: this.state.status === 'recording' ? 'warning' : 'info',
        });
        this.wakeLock = null;
      });
    } catch (error) {
      SentryService.addBreadcrumb({
        category: 'recording.wakelock',
        message: `Wake lock acquisition failed: ${error instanceof Error ? error.message : String(error)}`,
        level: 'warning',
      });
    }
  }

  private releaseWakeLock(): void {
    if (this.wakeLock) {
      SentryService.addBreadcrumb({
        category: 'recording.wakelock',
        message: 'Wake lock released (intentional)',
        level: 'info',
      });
      void this.wakeLock.release().catch(() => {});
      this.wakeLock = null;
    }
  }

  // ── Visibility Change ──

  private onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      // Flush buffered audio immediately when page goes hidden
      if (this.recorder && this.recorder.state === 'recording') {
        try {
          this.recorder.requestData();
        } catch {
          // Ignore
        }
      }
    } else {
      // Re-acquire Wake Lock when page becomes visible
      // (browsers release it when page is hidden)
      if (this.state.status === 'recording') {
        void this.acquireWakeLock();
      }
    }
  }

  // ── State Helpers ──

  private setServiceStatus(status: RecordingServiceStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.updateState({ status });
    this.emit('statusChanged', { status, prev });
  }

  private updateState(partial: Partial<RecordingState>): void {
    this.setState({ ...this.state, ...partial });
  }

  private handleError(message: string, cause?: unknown): void {
    this.setServiceStatus('error');
    this.updateState({ error: message });
    this.emit('error', { message, cause });

    SentryService.captureException(new Error(message));
  }
}
